import { SupabaseClient } from '@supabase/supabase-js'
import { supabase as supabaseClient } from '../client/browser'

export interface IntakeFormData {
  id: string
  userId: string
  healthRecordId?: string
  physicianName?: string
  physicianTel?: string
  occupation?: string
  education?: string
  maritalStatus?: string
  referralSource?: string
  referralNotes?: string
  culture?: string
  religion?: string
  issues?: string
  issuesImpact?: string
  healthyPast?: string
  emotionalIntensity?: number
  stressors?: string
  symptoms?: string
  trauma?: string
  isTherapyPast?: boolean
  therapyPast?: string
  isDiagnoses?: boolean
  diagnoses?: string
  isMedication?: boolean
  medication?: string
  isPhysical?: boolean
  physical?: string
  isSuicide?: boolean
  suicideCurrent?: string
  suicideHabit?: string
  isFamilyHistory?: boolean
  familyHistory?: string
  isSubstanceAbuse?: boolean
  substanceAbuse?: string
  isLegal?: boolean
  notes?: string
  // Couples-specific fields
  coupleIssues?: string
  coupleIssuesImpact?: string
  coupleIssuesPast?: string
  coupleEmotion?: number
  coupleCommunication?: number
  coupleCommitment?: number
  coupleLove?: number
  coupleSex?: number
  coupleFriend?: number
  coupleRespect?: number
  coupleConflict?: number
  coupleHome?: number
  coupleParent?: number
  coupleStressors?: string
  coupleConsent?: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Get all intake forms by user ID
 */
export async function getIntakeFormsByUserId(
  userId: string,
  client?: SupabaseClient
): Promise<IntakeFormData[]> {
  const supabase = client || supabaseClient
  
  try {
    const { data, error } = await supabase
      .from('intake_forms')
      .select('*')
      .eq('userId', userId)
      .is('deletedAt', null)
      .order('createdAt', { ascending: false })
    
    if (error) {
      console.error('[IntakeService] Error fetching intake forms:', error)
      return []
    }
    
    return data || []
  } catch (error) {
    console.error('[IntakeService] Error in getIntakeFormsByUserId:', error)
    return []
  }
}

/**
 * Get the latest intake form by user ID
 */
export async function getLatestIntakeFormByUserId(
  userId: string,
  client?: SupabaseClient
): Promise<IntakeFormData | null> {
  const supabase = client || supabaseClient
  
  try {
    const { data, error } = await supabase
      .from('intake_forms')
      .select('*')
      .eq('userId', userId)
      .is('deletedAt', null)
      .order('createdAt', { ascending: false })
      .limit(1)
      .single()
    
    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        console.log('[IntakeService] No intake form found for user:', userId)
        return null
      }
      console.error('[IntakeService] Error fetching intake form:', error)
      return null
    }
    
    return data
  } catch (error) {
    console.error('[IntakeService] Error in getLatestIntakeFormByUserId:', error)
    return null
  }
}

/**
 * Get all intake forms by health record ID
 */
export async function getIntakeFormsByHealthRecordId(
  healthRecordId: string,
  client?: SupabaseClient
): Promise<IntakeFormData[]> {
  const supabase = client || supabaseClient
  
  try {
    const { data, error } = await supabase
      .from('intake_forms')
      .select('*')
      .eq('healthRecordId', healthRecordId)
      .is('deletedAt', null)
      .order('createdAt', { ascending: false })
    
    if (error) {
      console.error('[IntakeService] Error fetching intake forms:', error)
      return []
    }
    
    return data || []
  } catch (error) {
    console.error('[IntakeService] Error in getIntakeFormsByHealthRecordId:', error)
    return []
  }
}

/**
 * Get all intake forms by episode (through user ID)
 */
export async function getIntakeFormsByEpisode(
  episodeId: string,
  client?: SupabaseClient
): Promise<IntakeFormData[]> {
  const supabase = client || supabaseClient
  
  try {
    // First get the episode to find the user ID
    const { data: episode, error: episodeError } = await supabase
      .from('episodes')
      .select('userId')
      .eq('id', episodeId)
      .single()
    
    if (episodeError || !episode) {
      console.error('[IntakeService] Error fetching episode:', episodeError)
      return []
    }
    
    // Then get all intake forms for that user
    return getIntakeFormsByUserId(episode.userId, supabase)
  } catch (error) {
    console.error('[IntakeService] Error in getIntakeFormsByEpisode:', error)
    return []
  }
}

/**
 * Get a specific intake form by ID
 */
export async function getIntakeFormById(
  intakeId: string,
  client?: SupabaseClient,
  includeUserData: boolean = false
): Promise<(IntakeFormData & { users?: any }) | null> {
  const supabase = client || supabaseClient

  try {
    if (includeUserData) {
      const { data, error } = await supabase
        .from('intake_forms')
        .select('*, users:userId(id,firstName,lastName,preferredName,email,phone,birthday,gender,pronoun,unit,street,city,province,country,postalCode,emergencyName,emergencyPhone)')
        .eq('id', intakeId)
        .is('deletedAt', null)
        .single()

      if (error) {
        console.error('[IntakeService] Error fetching intake form:', error)
        return null
      }

      return data as (IntakeFormData & { users?: any }) | null
    }

    const { data, error } = await supabase
      .from('intake_forms')
      .select('*')
      .eq('id', intakeId)
      .is('deletedAt', null)
      .single()

    if (error) {
      console.error('[IntakeService] Error fetching intake form:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('[IntakeService] Error in getIntakeFormById:', error)
    return null
  }
}